import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronDown, Tag, Copy, Check } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  const handleCopyCode = () => {
    navigator.clipboard.writeText('SAVE20');
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowCategories(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop All', path: '/shop' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Promo Code Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white text-xs sm:text-sm relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,.5)_50%,transparent_75%,transparent_100%)] bg-[length:200%_200%] animate-[shimmer_3s_linear_infinite]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-center gap-2 sm:gap-3 relative">
          <Tag className="w-4 h-4 flex-shrink-0" />
          <p className="font-semibold tracking-wide text-center">
            <span className="hidden sm:inline">Limited Time: </span>
            <span className="font-bold">20% OFF</span> your order with code
          </p>
          <button
            onClick={handleCopyCode}
            className="inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-2.5 py-1 rounded-md font-bold tracking-wider transition-all border border-white/40"
            aria-label="Copy promo code"
          >
            <span>SAVE20</span>
            {copiedCode ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          {copiedCode && (
            <span className="hidden sm:inline text-xs font-medium ml-1">Copied!</span>
          )}
        </div>
      </div>

      {/* Top Announcement Bar */}
      <div className="bg-indigo-700 text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="hidden sm:block tracking-wide">
            <span className="font-semibold">Free USA Shipping</span> &middot; 2-4 Day Delivery
          </p>
          <p className="tracking-wide">
            Need help? Call <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="font-semibold hover:underline">{CONTACT_INFO.phone}</a>
          </p>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="flex flex-col leading-tight">
                <span className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight">
                  EC <span className="text-indigo-600">Tech</span> Savvy
                </span>
                <span className="hidden sm:block text-[10px] tracking-[0.25em] uppercase text-gray-500 font-medium">
                  Tech Innovations Await
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                      isActive
                        ? 'text-indigo-600 bg-indigo-50'
                        : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}

              {/* Categories Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowCategories(true)}
                onMouseLeave={() => setShowCategories(false)}
              >
                <button className="px-4 py-2 rounded-md text-sm font-semibold text-gray-700 hover:text-indigo-600 hover:bg-gray-50 flex items-center gap-1">
                  Categories
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showCategories && (
                  <div className="absolute right-0 top-full pt-2 w-64">
                    <div className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/shop?category=${cat.slug}`}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Cart */}
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:text-indigo-600 hover:bg-gray-100 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-4 py-3 text-base font-semibold text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg"
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-3 border-t border-gray-200 mt-3">
                <p className="px-4 text-xs uppercase tracking-wider text-gray-500 mb-2">Categories</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.slug}`}
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
